import ListContainer from '../Common/ListContainer';
import LoadingSpinner from '../Common/LoadingSpinner';
import { Stack } from '@mui/material';
import ErrorBoundary from '../../hooks/errorBoundary';
import { DataGridPro, getGridDateOperators, getGridStringOperators, GridColDef, huHU } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { WorkerDeviceDetailsByTravel } from '../../@types/employeeData';
import { format } from 'date-fns';
import EmptyListMessage from '../Common/EmptyListMessage';
import { WorkerDeviceDataGridProps } from './WorkerTravelDetailsPage';


const columns: GridColDef[] = [
    {
        field: 'workplaceName',
        headerName: 'Munkahely',
        width: 170,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Munkahely</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
    {
        field: 'line',
        headerName: 'Járat',
        flex: 1,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Járat</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
    {
        field: 'arrivalAt',
        headerName: 'Érkezési idő',
        width: 230,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        type: 'dateTime',
        headerClassName: 'headerName',
        renderHeader: () => <strong>Érkezési idő</strong>,
        filterOperators: getGridDateOperators(true).filter(({ value }) => ['onOrAfter', 'onOrBefore'].includes(value)),
        valueFormatter: params => {
            return format(new Date(params.value as string), 'yyyy.MM.dd HH:mm:ss');
        },
    },
    {
        field: 'numberOfTravelers',
        headerName: 'Utasok száma',
        width: 170,
        sortable: true,
        filterable: false,
        headerAlign: 'right',
        align: 'right',
        renderHeader: () => <strong>Utasok száma</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
];

type LineDevicesMap = {
    [key: string]: WorkerDeviceDetailsByTravel[];
};

export const getLineDataGridId = (device: WorkerDeviceDetailsByTravel) => {
    return `${device.workplaceName}_${device.line}_${device.arrivalAt}`
}

export default function LineDataGrid({
                                         loading,
                                         workerDeviceDetailsByTravels,
                                         rowSelectionModel,
                                         setRowSelectionModel
                                     }: WorkerDeviceDataGridProps) {
    const [lineDeviceMap, setLineDeviceMap] = useState<LineDevicesMap>({});

    useEffect(() => {
        if (!workerDeviceDetailsByTravels?.length) {
            return;
        }

        const updatedDeviceMap: LineDevicesMap = {};

        workerDeviceDetailsByTravels.forEach((workerDeviceDetail) => {
            const line = getLineDataGridId(workerDeviceDetail)

            if (!updatedDeviceMap[line]) {
                updatedDeviceMap[line] = [];
            }
            updatedDeviceMap[line].push(workerDeviceDetail);
        });
        setLineDeviceMap(updatedDeviceMap);
    }, [workerDeviceDetailsByTravels]);

    return (
        <>
            {loading ? (
                <ListContainer>
                    <LoadingSpinner />
                </ListContainer>
            ) : (
                Object.entries(lineDeviceMap).length > 0 ? (
                    <Stack sx={{ height: 'calc(100vh - 500px)', mt: 1 }}>
                        <ErrorBoundary>
                            <DataGridPro
                                density={'compact'}
                                columns={columns}
                                rows={Object.entries(lineDeviceMap).map(([key, devices]) => ({
                                    id: key,
                                    workplaceName: devices[0].workplaceName,
                                    line: devices[0].line,
                                    arrivalAt: devices[0].arrivalAt,
                                    numberOfTravelers: devices.length,
                                }))}
                                initialState={{
                                    sorting: {
                                        sortModel: [
                                            { field: 'arrivalAt', sort: 'asc' },
                                            { field: 'workplaceName', sort: 'asc' },
                                            { field: 'line', sort: 'asc' },
                                        ],
                                    },
                                }}
                                rowHeight={35}
                                checkboxSelection
                                hideFooter={true}
                                getRowId={(row) => row.id}
                                rowSelectionModel={rowSelectionModel}
                                onRowSelectionModelChange={(selectedRows) => {
                                    setRowSelectionModel(selectedRows);
                                }}
                                localeText={huHU.components.MuiDataGrid.defaultProps.localeText}
                                unstable_headerFilters
                            />
                        </ErrorBoundary>
                    </Stack>
                ) : (
                    <ListContainer>
                        <EmptyListMessage />
                    </ListContainer>
                ))}
        </>
    );
};